/**
 * Pure, AWS-free, DOM-free presentation helpers for the storage UI.
 *
 * The storage pages are intentionally thin: all of the non-trivial display
 * logic — picklist grouping/sorting for physical retrieval, and the mapping
 * from a location-resolution status to a renderable descriptor — lives here as
 * pure exported functions so it can be unit-tested without a DOM testing
 * library (the frontend jest env is `node`). The React components import these
 * helpers and render the plain data structures they return.
 *
 * Nothing in this file imports React, a browser API, or an AWS SDK. The shapes
 * (`LocationResolution`, `PicklistEntry`, `LocationPath`) come from
 * `@tcvs/shared` and are never redeclared here.
 *
 * Validates: Requirements 7.3, 13.1, 13.2, 13.3
 */

import type { LocationPath, LocationResolution, PicklistEntry } from '../shared/types';

// ---------------------------------------------------------------------------
// Placement display descriptor
// ---------------------------------------------------------------------------

/**
 * The union of every placement status the UI can render. It is the union of
 * the `LocationResolution` statuses (`STORED` / `STORED_UNPLACED` / `PULLED` /
 * `NOT_PLACED` / `CARD_UNKNOWN`) and the picklist-only `UNKNOWN` status.
 */
export type PlacementStatus =
  | 'STORED'
  | 'STORED_UNPLACED'
  | 'PULLED'
  | 'NOT_PLACED'
  | 'UNKNOWN'
  | 'CARD_UNKNOWN';

/**
 * A pure, renderable description of a card's placement state: a human label, a
 * coarse tone the component can colour on, whether a full Location_Path should
 * be shown, and (when present) the path or the bare batch.
 */
export interface PlacementDisplay {
  status: PlacementStatus;
  /** Human-readable label for the state (e.g. "Stored", "Pulled"). */
  label: string;
  /** Coarse category the component maps to a colour/emphasis. */
  tone: 'stored' | 'unplaced' | 'pulled' | 'absent' | 'unknown';
  /** True only for `STORED`, where a full Shelf → Box → Batch path is present. */
  showPath: boolean;
  /** The full Location_Path; present only for `STORED`. */
  path?: LocationPath;
  /** The bare Storage_Batch; present only for `STORED_UNPLACED`. */
  batch?: { entityId: string };
  /** The containing Box when a partial `STORED_UNPLACED` chain has resolved that far. */
  box?: { entityId: string };
  /** The containing Row when a full or partial chain has resolved that far. */
  row?: { entityId: string; rowCode?: string };
  /** The containing Shelf when a partial `STORED_UNPLACED` chain has resolved that far. */
  shelf?: { entityId: string; shelfCode: string };
}

/**
 * Map a `LocationResolution` or a `PicklistEntry` to a {@link PlacementDisplay}.
 *
 * Both inputs are discriminated unions keyed on the same `status` field with the
 * same payload fields (`path` for `STORED`, `batch` for `STORED_UNPLACED`), so a
 * single total function covers both. The function is total over every status and
 * never throws (Requirements 13.1, 13.2, 13.3, 7.3).
 */
export function describePlacement(input: LocationResolution | PicklistEntry): PlacementDisplay {
  switch (input.status) {
    case 'STORED':
      return {
        status: 'STORED',
        label: 'Stored',
        tone: 'stored',
        showPath: true,
        path: input.path,
      };
    case 'STORED_UNPLACED':
      return {
        status: 'STORED_UNPLACED',
        label:
          input.box !== undefined
            ? 'Stored — box not on shelf'
            : input.row !== undefined
              ? 'Stored — row not in box'
              : 'Stored — batch not yet put away',
        tone: 'unplaced',
        showPath: false,
        ...(input.shelf !== undefined ? { shelf: input.shelf } : {}),
        ...(input.box !== undefined ? { box: input.box } : {}),
        ...(input.row !== undefined ? { row: input.row } : {}),
        batch: input.batch,
      };
    case 'PULLED':
      return {
        status: 'PULLED',
        label: 'Pulled (no longer in storage)',
        tone: 'pulled',
        showPath: false,
      };
    case 'NOT_PLACED':
      return {
        status: 'NOT_PLACED',
        label: 'Not yet stored',
        tone: 'absent',
        showPath: false,
      };
    case 'UNKNOWN':
      return {
        status: 'UNKNOWN',
        label: 'Unknown card id',
        tone: 'unknown',
        showPath: false,
      };
    case 'CARD_UNKNOWN':
      return {
        status: 'CARD_UNKNOWN',
        label: 'No card record',
        tone: 'unknown',
        showPath: false,
      };
    default: {
      // Exhaustiveness guard — keeps the function total if the union grows.
      const exhaustive: never = input;
      void exhaustive;
      return {
        status: 'UNKNOWN',
        label: 'Unknown',
        tone: 'unknown',
        showPath: false,
      };
    }
  }
}

// ---------------------------------------------------------------------------
// Placement action gating (card detail Pick / Restock buttons)
// ---------------------------------------------------------------------------

/**
 * Which physical placement action the card detail page should offer, derived
 * purely from the card's resolved location status:
 *   - `'pick'`    — the card is in storage (`STORED` / `STORED_UNPLACED`), so it
 *                   can be pulled (`POST /storage/pick`).
 *   - `'restock'` — the card is `PULLED`, so it can be put back into a batch
 *                   (`POST /storage/putaway`).
 *   - `'none'`    — no placement action applies (never placed, unknown, or the
 *                   location hasn't resolved): a card that was never put away has
 *                   nothing to pull, and an unknown/unresolved card offers nothing.
 *
 * Pure + total over every status; `undefined` (location not loaded) → `'none'`.
 */
export function placementAction(
  location: LocationResolution | undefined,
): 'pick' | 'restock' | 'none' {
  if (location === undefined) return 'none';
  switch (location.status) {
    case 'STORED':
    case 'STORED_UNPLACED':
      return 'pick';
    case 'PULLED':
      return 'restock';
    default:
      // NOT_PLACED / CARD_UNKNOWN (and any future status) → no action.
      return 'none';
  }
}

// ---------------------------------------------------------------------------
// Picklist grouping + sorting (shelf → box → batch)
// ---------------------------------------------------------------------------

/** The cards resolved to one Storage_Batch within a Box. */
export interface PicklistBatchGroup {
  batch: { entityId: string };
  /** Card ids in this batch, sorted ascending. */
  cardIds: string[];
}

/** The batches resolved within one Box on a Shelf. */
export interface PicklistBoxGroup {
  box: { entityId: string };
  /** Batches in this box, sorted by batch entityId. */
  batches: PicklistBatchGroup[];
}

/** The boxes resolved on one Shelf. */
export interface PicklistShelfGroup {
  shelf: { entityId: string; shelfCode: string };
  /** Boxes on this shelf, sorted by box entityId. */
  boxes: PicklistBoxGroup[];
}

/** A bucket for STORED_UNPLACED cards, grouped by their known Storage_Batch. */
export interface UnplacedBatchGroup {
  batch: { entityId: string };
  cardIds: string[];
}

/**
 * The fully grouped + sorted picklist, ready to render in physical-retrieval
 * order. `stored` is the shelf→box→batch tree to walk; the remaining buckets
 * collect the cards that have no full path (Requirement 7.3).
 */
export interface GroupedPicklist {
  /** STORED cards, grouped shelf → box → batch and sorted for retrieval. */
  stored: PicklistShelfGroup[];
  /** STORED in an unplaced batch — grouped by batch only, sorted. */
  storedUnplaced: UnplacedBatchGroup[];
  /** Pulled card ids, sorted ascending. */
  pulled: string[];
  /** Never-placed card ids, sorted ascending. */
  notPlaced: string[];
  /** Unknown card ids (no card record), sorted ascending. */
  unknown: string[];
}

/**
 * Group a picklist result by physical location (shelf → box → batch) and sort
 * it deterministically for physical retrieval (Requirement 7.3).
 *
 * STORED entries are grouped into the shelf→box→batch tree; STORED_UNPLACED,
 * PULLED, NOT_PLACED, and UNKNOWN entries are bucketed separately so a single
 * unresolved card never breaks the retrieval ordering. Sorting is total and
 * deterministic: shelves by `shelfCode` then `entityId`, boxes and batches by
 * `entityId`, and every card-id list ascending. Duplicate input ids are
 * preserved (the function does not dedupe), matching the picklist contract that
 * there is exactly one entry per input id.
 */
export function groupPicklist(entries: readonly PicklistEntry[]): GroupedPicklist {
  // Build the shelf→box→batch tree in maps keyed by id for O(n) grouping, then
  // materialise sorted arrays at the end so the result is deterministic.
  const shelves = new Map<
    string,
    {
      shelf: { entityId: string; shelfCode: string };
      boxes: Map<string, { box: { entityId: string }; batches: Map<string, string[]> }>;
    }
  >();
  const unplaced = new Map<string, string[]>();
  const pulled: string[] = [];
  const notPlaced: string[] = [];
  const unknown: string[] = [];

  for (const entry of entries) {
    switch (entry.status) {
      case 'STORED': {
        const { shelf, box, batch } = entry.path;
        let shelfNode = shelves.get(shelf.entityId);
        if (shelfNode === undefined) {
          shelfNode = { shelf, boxes: new Map() };
          shelves.set(shelf.entityId, shelfNode);
        }
        let boxNode = shelfNode.boxes.get(box.entityId);
        if (boxNode === undefined) {
          boxNode = { box, batches: new Map() };
          shelfNode.boxes.set(box.entityId, boxNode);
        }
        const batchCards = boxNode.batches.get(batch.entityId);
        if (batchCards === undefined) {
          boxNode.batches.set(batch.entityId, [entry.cardId]);
        } else {
          batchCards.push(entry.cardId);
        }
        break;
      }
      case 'STORED_UNPLACED': {
        const cards = unplaced.get(entry.batch.entityId);
        if (cards === undefined) {
          unplaced.set(entry.batch.entityId, [entry.cardId]);
        } else {
          cards.push(entry.cardId);
        }
        break;
      }
      case 'PULLED':
        pulled.push(entry.cardId);
        break;
      case 'NOT_PLACED':
        notPlaced.push(entry.cardId);
        break;
      case 'UNKNOWN':
        unknown.push(entry.cardId);
        break;
      default: {
        const exhaustive: never = entry;
        void exhaustive;
        break;
      }
    }
  }

  const stored: PicklistShelfGroup[] = [...shelves.values()]
    .map((shelfNode) => ({
      shelf: shelfNode.shelf,
      boxes: [...shelfNode.boxes.values()]
        .map((boxNode) => ({
          box: boxNode.box,
          batches: [...boxNode.batches.entries()]
            .map(([batchId, cardIds]) => ({
              batch: { entityId: batchId },
              cardIds: [...cardIds].sort(compareStrings),
            }))
            .sort((a, b) => compareStrings(a.batch.entityId, b.batch.entityId)),
        }))
        .sort((a, b) => compareStrings(a.box.entityId, b.box.entityId)),
    }))
    .sort(compareShelfGroups);

  const storedUnplaced: UnplacedBatchGroup[] = [...unplaced.entries()]
    .map(([batchId, cardIds]) => ({
      batch: { entityId: batchId },
      cardIds: [...cardIds].sort(compareStrings),
    }))
    .sort((a, b) => compareStrings(a.batch.entityId, b.batch.entityId));

  return {
    stored,
    storedUnplaced,
    pulled: [...pulled].sort(compareStrings),
    notPlaced: [...notPlaced].sort(compareStrings),
    unknown: [...unknown].sort(compareStrings),
  };
}

/** Total, locale-independent string comparison for deterministic ordering. */
function compareStrings(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

/** Sort shelves by human-readable `shelfCode`, then by opaque `entityId`. */
function compareShelfGroups(a: PicklistShelfGroup, b: PicklistShelfGroup): number {
  const byCode = compareStrings(a.shelf.shelfCode, b.shelf.shelfCode);
  if (byCode !== 0) return byCode;
  return compareStrings(a.shelf.entityId, b.shelf.entityId);
}
